/* eslint-disable max-lines */
import { BaseResponse } from '@nxte-nl/apollo-hooks';

import { ApplePayData } from '../components/ActivationForm/ActivationForm';
import { AdyenBlikFormFields } from '../components/AdyenBlikForm/AdyenBlikForm';
import {
  CheckoutBillingFields } from '../components/AdyenCheckoutBillingForm/AdyenCheckoutBillingForm';
import {
  FormFields as BasketOrderFormFields,
} from '../components/BasketGuestOrderForm/BasketGuestOrderForm';
import {
  CreditCardFormFields,
} from '../components/CreditCardForm/CreditCardForm';
import { VocabularyPresentational } from '../components/FilterSection/FilterSection';
import { AccountOrdersPerYear } from '../components/OrdersPerYear/OrdersPerYear';
import { ProductDetailsFaqInfo } from '../components/ProductDetailsFaq/ProductDetailsFaq';
import { GiftcardProductPageType } from '../components/ProductOrder/ProductOrder';
import {
  BlankGiftCardPackProductVariationPresentational,
} from '../components/ReorderProductsBlock/ReorderProductsBlock';
import { CreditCardType } from '../constants/forms';
import {
  AdyenCreditCardFormFields,
} from '../containers/AdyenCreditCardFormContainer/AdyenCreditCardFormContainer';
import {
  PackageItemId,
} from '../containers/BasketDeleteItemModalContainer/BasketDeleteItemModalContainer';
import { UserOrders } from '../hooks/accountOrderHooks/accountOrderHooks';
import { UserPreFillData } from './Account';
import { ItemToEdit, ItemToDelete } from './Basket';
import { FeatureToggleValues } from './FeatureToggles';
import { ModalTypes } from './Modals';
import { Layout, LayoutExtension } from './ResponsiveState';
import { BusinessFormPageV2 } from './apiCompoundBusinessPage';
import { CartResponse, DeliveryMethod, DeliveryMethodExtended } from './apiCompoundCommon';
import { DoublerThankYouPage } from './apiCompoundDoubler';
import { ProductsResponse, TopBlock } from './apiCompoundProduct';
import {
  OriginalPackagePrice,
  DeliveryInfo,
  GiftcardCategory,
  Address,
  AddressInput,
  GiftcardProduct,
  CB_GivingMomentsPage,
  CB_Blog,
  PersonalMessageProductVariation,
  CB_GivingMomentsOverviewPage,
  CB_BlogArticle,
  CB_BusinessFormPage,
  CB_HomePage,
  CB_ContactsPage,
  CB_ProductsOverviewPage,
  CB_TermsConditionsPage,
  PopularSearchResults,
  CB_RedeemCheckPage,
  CB_RedeemResultPage,
  CB_RedeemB2BResultPage,
  GiftCardBrand,
  RedeemOrder,
  GiftCardData,
  RedeemPackage,
  OrderResponse,
  StoreData,
  RedeemData,
  DoublerResponse,
  CB_LocationsOverviewPage,
  LocationsResponse,
  Restaurant,
  CB_UserHomePage,
  UserResponse,
  GeoLocationRectangle,
  CB_GiftCardActivationPage,
  CB_GiftCardActivationThankYouPage,
  CB_BlankGiftCardsPage,
  CB_HomePage_v2,
  CB_LandingPage,
  PackageAddingMode,
  GiftcardPackageDataInput,
  GiftPackingProduct,
  TaxonomyFilterInput,
  ShippingMethod,
  CB_BusinessFormPage_v3,
  CB_BusinessSubPage,
  GiftcardOrder,
  CB_LandingPageV2,
} from './apiTypes';
import {
  DeliveryMethodType,
  ErrorPresentational,
  GiftcardProductVariationPresentational,
  IpLocation,
  GiftPackingProductPresentational,
  PackageItemsListWrapper,
  RedeemGuestModalType,
  RedeemSelectedProduct,
  DigitalWrapperPresentational, ClientLanguageLink,
} from './common';

export type PageEntityData =
  CB_HomePage
  | CB_HomePage_v2
  | GiftcardProduct
  | CB_ProductsOverviewPage
  | CB_GivingMomentsPage
  | CB_GivingMomentsOverviewPage
  | CB_BusinessFormPage
  | BusinessFormPageV2
  | CB_BusinessFormPage_v3
  | CB_BusinessSubPage
  | CB_TermsConditionsPage
  | CB_ContactsPage
  | CB_Blog
  | CB_BlogArticle
  | CB_RedeemResultPage
  | CB_RedeemB2BResultPage
  | CB_RedeemCheckPage
  | GiftcardCategory
  | GiftCardBrand
  | DoublerThankYouPage
  | CB_LocationsOverviewPage
  | Restaurant
  | CB_UserHomePage
  | CB_GiftCardActivationPage
  | CB_GiftCardActivationThankYouPage
  | CB_BlankGiftCardsPage
  | CB_LandingPage
  | CB_LandingPageV2;

export type PageEntity = BaseResponse<PageEntityData>;

export enum GlobalMessages {
  APIErrorModal = 'APIErrorModal',
  CartQuantity = 'cartQuantity',
  ErrorModal = 'errorModal',
  FeatureToggles = 'featureToggles',
  FeatureTogglesLoaded = 'FeatureTogglesLoaded',
  IpLocation = 'ipLocation',
  Language = 'language',
  Layout = 'layout',
  LayoutExtension = 'layoutExtension',
  MutationInProgress = 'mutationInProgress',
  OpenModals = 'openModals',
  PageContentLoading = 'PageContentLoading',
  PageEntity = 'pageEntity',
  PageEntityLoading = 'pageEntityLoading',
  PageType = 'pageType',
  RedeemToken = 'redeemToken',
}

export enum CheckoutMessages {
  AddressToPrefill = 'addressToPrefill',
  AdyenBlikFormData = 'AdyenBlikFormData',
  AdyenCardFormData = 'AdyenCardFormData',
  ApplePayData = 'ApplePayData',
  CheckoutBillingInfo = 'CheckoutBillingInfo',
  CreditCardFormData = 'CreditCardFormData',
  CreditCardType = 'CreditCardType',
  CurrentReorderGiftCard = 'CurrentReorderGiftCard',
  DeliveryMethod = 'deliveryMethod',
  DeliveryMethodChanged = 'DeliveryMethodChanged',
  DeliveryMethodType = 'deliveryMethodType',
  DeliveryMethods = 'deliveryMethods',
  FaqInfo = 'FaqInfo',
  GiftcardProductPageType = 'GiftcardProductPageType',
  OnAddPackageToCart = 'onAddPackageToCart',
  PersonalNoteProduct = 'personalNoteProduct',
  ProductsQuantity = 'checkoutQuantity',
  ReorderVariation = 'ReorderVariation',
  SelectedAddingMode = 'SelectedAddingMode',
  SelectedGiftPacking = 'selectedGiftPacking',
  SelectedWrapper = 'selectedWrapper',
  ShowHand = 'ShowHand',
  Variation = 'variation',
  Wrapper = 'wrapper',
  isAdyenBlikCodeValid = 'isAdyenBlikCodeValid',
  isCheckoutBillingInfoValid = 'isCheckoutBillingInfoValid',
}

export enum ProductOverviewModalMessages {
  AddressFormDisabled = 'addressFormDisabled',
  AddressToPrefill = 'addressToPrefill',
  DeliveryInfo = 'deliveryInfo',
  DeliveryInfoResponse = 'deliveryInfoResponse',
  DigitalAddress = 'digitalAddress',
  DigitalAddressFormDisabled = 'digitalAddressFormDisabled',
  DigitalAddressToPrefill = 'digitalAddressToPrefill',
  DigitalDeliveryFormValid = 'digitalDeliveryFormValid',
  GiftPackings = 'giftPackings',
  IsOrderExists = 'IsOrderExists',
  PackageData = 'packageData',
  PersonalNote = 'personalNote',
  PersonalNoteProduct = 'personalNoteProduct',
  PersonalNoteToPrefill = 'personalNoteToPrefill',
  PhysicalAddress = 'physicalAddress',
  PhysicalDeliveryFormValid = 'physicalDeliveryFormValid',
  Price = 'price',
  ReceiverName = 'receiverName',
  ReceiverNameToPrefill = 'receiverNameToPrefill',
  SelectedDeliveryInfo = 'selectedDeliveryInfo',
  SelectedShipmentMethod = 'selectedShipmentMethod',
  ShipmentMethods = 'shipmentMethods',
}

export enum RedeemMessages {
  IsExternalLinks = 'IsExternalLinks',
  PackageToDelete = 'PackageToDelete',
  RedeemCardData = 'RedeemCardData',
  RedeemData = 'RedeemData',
  RedeemDoublerModalOpened = 'RedeemDoublerModalOpened',
  RedeemOrderResponse = 'RedeemOrderResponse',
  RedeemSelectedProduct = 'RedeemSelectedProduct',
  RedeemSelectedProductPrice = 'RedeemSelectedProductPrice',
  ShouldAddDigitalAddress = 'ShouldAddDigitalAddress',
  ShouldAddPhysicalAddress = 'ShouldAddPhysicalAddress',
}

export enum DoublerMessages {
  DoublerResponse = 'DoublerResponse',
}

export enum BasketMessages {
  Cart = 'cart',
  ItemToEdit = 'itemToEdit',
  OrderModalFormData = 'orderModalFormData',
  isCartSet = 'isCartSet',
}

export enum RedeemCartMessages {
  Loading = 'loadingRedeemCart',
  RedeemCart = 'redeemCart',
  RedeemCartPackages = 'redeemCartPackages',
  UnusedCardIndex = 'unusedCardIndex',
}

export enum BasketDeleteItemModalMessages {
  ItemToDelete = 'itemToDelete',
}

export enum CheckoutAddressEditMessages {
  CheckoutAddressEditing = 'checkoutAddressEditing',
}

export enum RedeemCheckoutAddressEditMessages {
  RedeemCheckoutAddressEditMessages = 'redeemCheckoutAddressEditMessages',
}

export enum AppDataGiftCardCategories {
  Categories = 'categories',
  Loading = 'loadingGiftCardCategories',
}

export enum AppDataPopularSearchResults {
  Loading = 'loadingPopularSearchResults',
  PopularSearchResults = 'popularSearchResults',
}

export enum AppDataStoreData {
  Loading = 'loadingStoreData',
  StoreData = 'storeData',
}

export enum ProductsListMessages {
  BlocksQueue = 'blocksQueue',
  BlocksQueueLoading = 'blocksQueueLoading',
  Products = 'products',
  ProductsLoading = 'productsLoading',
}

export enum Filters {
  AppliedFiltersAmount = 'AppliedFiltersAmount',
  AvailableFilters = 'availableFilters',
  SelectedFilters = 'SelectedFilters',
  TotalItems = 'totalItems',
}

export enum CustomerSupportMessages {
  IframeLoaded = 'iframeLoaded',
}

export enum RedeemConfirmationMessages {
  IframeLoaded = 'redeemIframeLoaded',
}

export enum MapMessages {
  LocationsListLoading = 'LocationsListLoading',
  MapRectangleArea = 'MapRectangleArea',
  PDPLocations = 'PDPLocations',
}

export enum AccountMessages {
  AccountForgotPasswordEmail = 'AccountForgotPasswordEmail',
  AccountOrderDetailsModalItem = 'AccountOrderDetailsModalItem',
  AccountOrdersSplittedPerYear = 'AccountOrdersSplittedPerYear',
  AccountUserInfo = 'AccountUserInfo',
  AccountUserInfoLoading = 'AccountUserInfoLoading',
  AccountUserPreFillData = 'AccountUserPreFillData',
}

export type AccoutReorder = {
  order: GiftcardOrder;
  packageId: string;
  packageIds: string[];
};

export enum StoreSwitcherMessages {
  LanguageLinks = 'languageLinks',
  SelectedCountry = 'SelectedCountry',
  isNonBlockPart = 'isNonBlockPart',
}

export enum RefsMessages {
  FooterRef = 'FooterRef',
}

export type Messages = {
  [CheckoutMessages.ProductsQuantity]: number;
  [CheckoutMessages.Variation]: GiftcardProductVariationPresentational | null;
  [CheckoutMessages.SelectedWrapper]: PackageItemsListWrapper | DigitalWrapperPresentational | null;
  [CheckoutMessages.SelectedGiftPacking]: GiftPackingProductPresentational | null;
  [CheckoutMessages.DeliveryMethod]: DeliveryMethod;
  [CheckoutMessages.DeliveryMethodType]: DeliveryMethodType | null;
  [CheckoutMessages.DeliveryMethods]: DeliveryMethodExtended[] | null;
  [CheckoutMessages.FaqInfo]: ProductDetailsFaqInfo | null;
  [CheckoutMessages.GiftcardProductPageType]: GiftcardProductPageType | null;
  [CheckoutMessages.CurrentReorderGiftCard]: BlankGiftCardPackProductVariationPresentational | null;
  [CheckoutMessages.ReorderVariation]: BlankGiftCardPackProductVariationPresentational | null;
  [CheckoutMessages.SelectedAddingMode]: PackageAddingMode | null;
  [CheckoutMessages.CreditCardFormData]: CreditCardFormFields | null;
  [CheckoutMessages.AdyenCardFormData]: AdyenCreditCardFormFields | null;
  [CheckoutMessages.CheckoutBillingInfo]: Partial<CheckoutBillingFields>;
  [CheckoutMessages.AdyenBlikFormData]: Partial<AdyenBlikFormFields>;
  [CheckoutMessages.isCheckoutBillingInfoValid]: boolean;
  [CheckoutMessages.isAdyenBlikCodeValid]: boolean;
  [CheckoutMessages.CreditCardType]: CreditCardType | null;
  [CheckoutMessages.ApplePayData]: ApplePayData | null;

  [ProductOverviewModalMessages.PhysicalDeliveryFormValid]: boolean | null;
  [ProductOverviewModalMessages.DigitalDeliveryFormValid]: boolean | null;
  [ProductOverviewModalMessages.Price]: OriginalPackagePrice | undefined;
  [ProductOverviewModalMessages.DeliveryInfo]: DeliveryInfo[];
  [ProductOverviewModalMessages.SelectedDeliveryInfo]: DeliveryInfo | null;
  [ProductOverviewModalMessages.PhysicalAddress]: AddressInput | null;
  [ProductOverviewModalMessages.DigitalAddress]: AddressInput | null;
  [ProductOverviewModalMessages.ReceiverName]: string;
  [ProductOverviewModalMessages.PersonalNote]: string | null;
  [ProductOverviewModalMessages.AddressToPrefill]: Address;
  [ProductOverviewModalMessages.DigitalAddressToPrefill]: Address;
  [ProductOverviewModalMessages.ReceiverNameToPrefill]: string;
  [ProductOverviewModalMessages.AddressFormDisabled]: boolean;
  [ProductOverviewModalMessages.DigitalAddressFormDisabled]: boolean;
  [ProductOverviewModalMessages.PersonalNoteProduct]: PersonalMessageProductVariation | null;
  [ProductOverviewModalMessages.PackageData]: GiftcardPackageDataInput | null;
  [ProductOverviewModalMessages.IsOrderExists]: boolean;
  [ProductOverviewModalMessages.GiftPackings]: GiftPackingProduct[] | null;
  [ProductOverviewModalMessages.ShipmentMethods]: ShippingMethod[] | null;
  [ProductOverviewModalMessages.SelectedShipmentMethod]: ShippingMethod | null;

  [CheckoutAddressEditMessages.CheckoutAddressEditing]: boolean;
  [RedeemCheckoutAddressEditMessages.RedeemCheckoutAddressEditMessages]: boolean;

  [BasketMessages.Cart]: CartResponse | null;
  [BasketMessages.ItemToEdit]: ItemToEdit | null;
  [BasketMessages.isCartSet]: boolean;
  [BasketMessages.OrderModalFormData]: Omit<
    BasketOrderFormFields,
    'isPasswordRequired' | 'password' | 'confirmPassword'
  > | null;

  [RedeemMessages.RedeemDoublerModalOpened]: boolean;
  [RedeemMessages.RedeemCardData]: GiftCardData | null;
  [RedeemMessages.RedeemData]: RedeemData | null;
  [RedeemMessages.RedeemOrderResponse]: OrderResponse | null;
  [RedeemMessages.PackageToDelete]: PackageItemId | null;
  [RedeemMessages.RedeemSelectedProduct]: RedeemSelectedProduct | null;
  [RedeemMessages.RedeemSelectedProductPrice]: string | undefined;
  [RedeemMessages.ShouldAddPhysicalAddress]: boolean;
  [RedeemMessages.ShouldAddDigitalAddress]: boolean;
  [RedeemMessages.IsExternalLinks]: boolean;

  [DoublerMessages.DoublerResponse]: DoublerResponse | null;

  [BasketDeleteItemModalMessages.ItemToDelete]: ItemToDelete | null;

  [RedeemCartMessages.RedeemCart]: RedeemOrder | null;
  [RedeemCartMessages.RedeemCartPackages]: RedeemPackage[] | null;
  [RedeemCartMessages.Loading]: boolean;
  [RedeemCartMessages.UnusedCardIndex]: number | null;

  [GlobalMessages.CartQuantity]: CartResponse | null;
  [GlobalMessages.Layout]: Layout;
  [GlobalMessages.LayoutExtension]: LayoutExtension;
  [GlobalMessages.OpenModals]: ModalTypes[];
  [GlobalMessages.APIErrorModal]: ErrorPresentational | null;
  [GlobalMessages.PageEntity]: PageEntity;
  [GlobalMessages.PageEntityLoading]: boolean;
  [GlobalMessages.PageType]: string | null;
  [GlobalMessages.PageContentLoading]: boolean;
  [GlobalMessages.MutationInProgress]: boolean;
  [GlobalMessages.RedeemToken]: string | null;
  [GlobalMessages.FeatureToggles]: FeatureToggleValues;
  [GlobalMessages.FeatureTogglesLoaded]: boolean;
  [GlobalMessages.Language]: string;
  [GlobalMessages.IpLocation]: IpLocation | null;

  [AppDataGiftCardCategories.Categories]: GiftcardCategory[] | null;
  [AppDataGiftCardCategories.Loading]: boolean;

  [AppDataPopularSearchResults.PopularSearchResults]: PopularSearchResults | null;
  [AppDataPopularSearchResults.Loading]: boolean;

  [AppDataStoreData.StoreData]: StoreData | null;
  [AppDataStoreData.Loading]: boolean;

  [ProductsListMessages.BlocksQueue]: TopBlock[] | null;
  [ProductsListMessages.BlocksQueueLoading]: boolean;
  [ProductsListMessages.Products]: ProductsResponse | null;
  [ProductsListMessages.ProductsLoading]: boolean;

  [CustomerSupportMessages.IframeLoaded]: boolean;

  [RedeemConfirmationMessages.IframeLoaded]: boolean;

  [MapMessages.PDPLocations]: LocationsResponse | null;
  [MapMessages.LocationsListLoading]: boolean | null;
  [MapMessages.MapRectangleArea]: GeoLocationRectangle | null;

  [AccountMessages.AccountForgotPasswordEmail]: string;
  [AccountMessages.AccountUserInfo]: UserResponse | null;
  [AccountMessages.AccountUserInfoLoading]: boolean;
  [AccountMessages.AccountOrderDetailsModalItem]: UserOrders | null;
  [AccountMessages.AccountUserPreFillData]: UserPreFillData;
  [AccountMessages.AccountOrdersSplittedPerYear]: AccountOrdersPerYear[] | null;

  [StoreSwitcherMessages.SelectedCountry]: string | null;
  [StoreSwitcherMessages.isNonBlockPart]: boolean;
  [StoreSwitcherMessages.LanguageLinks]: ClientLanguageLink[] | null;

  [Filters.AvailableFilters]: VocabularyPresentational[];
  [Filters.TotalItems]: number;
  [Filters.AppliedFiltersAmount]: number;
  [Filters.SelectedFilters]: TaxonomyFilterInput[];

  [RefsMessages.FooterRef]: React.RefObject<HTMLDivElement>;
};

export enum GlobalSignals {
  OnChangeModals = 'OnChangeModals',
  OnUpdateCartQuantity = 'onUpdateCartQuantity',
  RedeemTokenUpdated = 'RedeemTokenUpdated',
}

export enum ProductOverviewModalSignals {
  OnNextStep = 'onNextStep',
}
export enum BasketSignals {
  OnUpdateBasketItem = 'onUpdateBasketItem',
}
export enum RedeemSignals {
  OnRedeemLinkClick = 'OnRedeemLinkClick',
  onRedeemGuestOrderModalOpen = 'onRedeemGuestOrderModalOpen',
}
export enum AccountSignals {
  ReorderPackage = 'ReorderPackage',
  ReorderPackageFromModal = 'ReorderPackageFromModal',
}

export type Signals = {
  [GlobalSignals.OnChangeModals]: ModalTypes[];
  [GlobalSignals.RedeemTokenUpdated]: string | null;
  [GlobalSignals.OnUpdateCartQuantity]: void;

  [ProductOverviewModalSignals.OnNextStep]: void;
  [BasketSignals.OnUpdateBasketItem]: void;

  [RedeemSignals.onRedeemGuestOrderModalOpen]: RedeemGuestModalType;
  [RedeemSignals.OnRedeemLinkClick]: string;

  [AccountSignals.ReorderPackage]: AccoutReorder;
  [AccountSignals.ReorderPackageFromModal]: AccoutReorder;
};
